import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepicker, MatDatepickerInputEvent } from '@angular/material/datepicker';
import { isMasked } from '@miq/utils';
import moment from 'moment';
import { Moment } from 'moment';
import { validateYearInput, isNumericAndWithinMax, YEAR_VALIDATION_CONFIG } from './year-validation.util';

export interface SelectionChange {
  id: string;
  year?: number | string | null;
  date?: Moment | string | null;
}

const YEAR_PICKER_FORMATS = {
  parse: {
    dateInput: 'YYYY',
  },
  display: {
    dateInput: 'YYYY',
    monthYearLabel: 'YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'YYYY',
  },
};

@Component({
  selector: 'miq-year-picker',
  template: `
    <div class="mff-year-picker-container">
      <mat-form-field
        class="mff-year-picker"
        [ngClass]="{ customClass, 'mff-year-picker-backgound': !disabled }"
        appearance="outline"
        (click)="onClickInput(yearPicker)"
      >
        <input
          #yearInput
          matInput
          autocomplete="no-autofill"
          type="text"
          [id]="'e2e-miq-year-picker-' + id"
          [disabled]="disabled || readonly"
          [readonly]="readonly"
          [maxLength]="4"
          [(ngModel)]="date"
          [matDatepicker]="yearPicker"
          [placeholder]="placeholder"
          [max]="maxDate"
          [min]="minDate"
          [matDatepickerFilter]="dateFilter"
          miqOnlyNumbers
          [restrictedKeyCodes]="restrictedKeyCodesByType"
          [allowPaste]="allowPaste"
          (dateChange)="onDateChange($event)"
          (blur)="onInputBlur($event)"
          (paste)="onPaste($event)"
        />
        <mat-datepicker-toggle matSuffix [for]="yearPicker"></mat-datepicker-toggle>
        <mat-datepicker
          #yearPicker
          startView="multi-year"
          panelClass="mff-year-picker-panel-class"
          [disabled]="disabled"
          (yearSelected)="onYearSelected($event, yearPicker)"
        ></mat-datepicker>
      </mat-form-field>
    </div>
  `,
  styleUrls: ['./year-picker.component.scss'],
  providers: [{ provide: MAT_DATE_FORMATS, useValue: YEAR_PICKER_FORMATS }],
  standalone: false,
})
export class YearPickerComponent {
  @Input() id: string;
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() minDate: string | Moment | null = null;
  @Input() maxDate: string | Moment | null = null;
  @Input() customClass = '';
  @Input() readonly = false;
  @Input() required = false;
  @Input() allowPaste = false;
  @Input() disableYearBoundsValidation = false;

  @Output() clearIcon = new EventEmitter<any>();
  @Output() selectionChange = new EventEmitter<SelectionChange>();

  @ViewChild('yearPicker', { static: false }) yearPicker: MatDatepicker<Moment>;
  @ViewChild('yearInput', { static: false }) yearInput: any;

  private _dateValue: string | moment.Moment;
  restrictedKeyCodesByType = [110, 190];

  dateFilter = (date: Moment | null): boolean => {
    if (!date) return true;
    if (this.disableYearBoundsValidation) {
      return true;
    }
    const year = date.year();
    return year >= YEAR_VALIDATION_CONFIG.MIN_YEAR && year <= YEAR_VALIDATION_CONFIG.MAX_YEAR;
  };

  @Input() set date(date: string | moment.Moment | number | null) {
    if (date === null || date === undefined) {
      this._dateValue = null;
      this.clearInputField();
      return;
    }

    if (typeof date !== 'number' && (isMasked(date) || moment.isMoment(date))) {
      this._dateValue = date;
      return;
    }

    this._dateValue = date && typeof date === 'number' ? moment().day(1).month(1).year(date) : null;
  }

  get date(): string | moment.Moment {
    return this._dateValue;
  }

  onYearSelected(selectedDate: Moment, datepicker: MatDatepicker<Moment>) {
    datepicker.close();
    const year = selectedDate?.year();
    const raw = year?.toString() || '';
    this.validateAndEmit(raw);
  }

  onDateChange(change: MatDatepickerInputEvent<Moment>) {
    const input = change.targetElement as HTMLInputElement;
    const rawInput = input?.value?.trim() || '';

    if (rawInput.length > 0 && rawInput.length < 4) {
      this._dateValue = null;
      this.clearInputField();
      return;
    }

    if (rawInput.length === 4 && moment.isMoment(change.value)) {
      this.validateAndEmit(rawInput);
    }
  }

  onPaste(event: ClipboardEvent) {
    if (!this.allowPaste) {
      return;
    }
    const pasted = event.clipboardData?.getData('text') || '';
    if (!isNumericAndWithinMax(pasted)) {
      event.preventDefault();
      return;
    }
    if (this.disableYearBoundsValidation) {
      const year = parseInt(pasted, 10);
      if (!isNaN(year)) {
        this._dateValue = moment().day(1).month(0).year(year);
        this.selectionChange.emit({
          id: this.id,
          year: year,
          date: this._dateValue,
        });
      }
      event.preventDefault();
      return;
    }
    const year = parseInt(pasted, 10);
    if (!isNaN(year)) {
      const { minYear, maxYear } = this.getMinMaxYears();

      if (year < minYear || year > maxYear) {
        event.preventDefault();
      }
    }
  }

  onInputBlur(event: FocusEvent) {
    const input = event.target as HTMLInputElement;
    const raw = (input?.value || '').trim();
    this.validateAndEmit(raw);
  }

  private getMinMaxYears(): { minYear: number; maxYear: number } {
    if (this.disableYearBoundsValidation) {
      return { minYear: -99999, maxYear: 99999 };
    }
    const minYear =
      this.minDate !== null && moment.isMoment(this.minDate) ? this.minDate.year() : YEAR_VALIDATION_CONFIG.MIN_YEAR;
    const maxYear =
      this.maxDate !== null && moment.isMoment(this.maxDate) ? this.maxDate.year() : YEAR_VALIDATION_CONFIG.MAX_YEAR;
    return { minYear, maxYear };
  }

  private validateAndEmit(raw: string) {
    const { minYear, maxYear } = this.getMinMaxYears();
    const validationResult = validateYearInput(raw, minYear, maxYear);

    if (!validationResult.isValid) {
      this._dateValue = null;
      this.clearInputField();
      if (raw === '') {
        this.selectionChange.emit({ id: this.id, year: null, date: null });
      }
      return;
    }

    this._dateValue = validationResult.momentDate;
    this.selectionChange.emit({
      id: this.id,
      year: validationResult.year,
      date: validationResult.momentDate,
    });
  }

  onClickInput(yearPicker) {
    if (this.readonly && !this.disabled) {
      yearPicker?.open();
    }
  }

  private clearInputField() {
    if (this.yearInput && this.yearInput.nativeElement) {
      this.yearInput.nativeElement.value = '';
    }
  }
}
